import type { Profile, SessionResponse } from "@frontier/protocol/contracts";
import type {
  RegisterRequest,
  LoginRequest,
  FactionRequest,
} from "@frontier/protocol/auth-contracts";
import { ApiError } from "./session-api";

export interface AuthSnapshot {
  status: "loading" | "ready" | "signed-out" | "recovery";
  session: SessionResponse | null;
  busy: boolean;
  error: string;
  logoutPending: boolean;
  retryAvailable: boolean;
}
export interface AuthApi {
  session(token: string): Promise<SessionResponse>;
  register(token: string, input: RegisterRequest): Promise<SessionResponse>;
  login(input: LoginRequest): Promise<SessionResponse>;
  logout(token: string): Promise<{ ok: true }>;
  chooseFaction(token: string, input: FactionRequest): Promise<SessionResponse>;
}
export const INITIAL_AUTH: AuthSnapshot = {
  status: "loading",
  session: null,
  busy: false,
  error: "",
  logoutPending: false,
  retryAvailable: false,
};

/** Only canonical responses replace identity; each intent invalidates older asynchronous work. */
export class AuthSession {
  private epoch = 0;
  private value: AuthSnapshot = INITIAL_AUTH;
  private nickname: {
    playerId: string;
    name: string;
    revision: number;
  } | null = null;
  private bootstrapRequest: {
    token: string;
    request: Promise<SessionResponse>;
  } | null = null;
  constructor(
    private readonly api: AuthApi,
    private readonly storage: Pick<
      Storage,
      "getItem" | "setItem" | "removeItem"
    >,
    private readonly publish: (state: AuthSnapshot) => void,
    private readonly invalidate: () => void,
  ) {}
  snapshot(): AuthSnapshot {
    return this.value;
  }
  observeNickname(token: string, profile: Profile): boolean {
    const session = this.value.session;
    if (
      this.value.status !== "ready" ||
      this.value.busy ||
      !session ||
      session.token !== token ||
      session.profile.playerId !== profile.playerId ||
      profile.nameRevision === undefined ||
      profile.nameRevision <=
        Math.max(
          session.profile.nameRevision ?? -1,
          this.nickname?.playerId === profile.playerId
            ? this.nickname.revision
            : -1,
        )
    )
      return false;
    this.nickname = {
      playerId: profile.playerId,
      name: profile.name,
      revision: profile.nameRevision,
    };
    return true;
  }
  private withNickname(session: SessionResponse): SessionResponse {
    const { profile } = session;
    const cached = this.nickname;
    if (
      cached?.playerId === profile.playerId &&
      cached.revision >= (profile.nameRevision ?? -1)
    )
      return {
        ...session,
        profile: {
          ...profile,
          name: cached.name,
          nameRevision: cached.revision,
        },
      };
    this.nickname =
      profile.nameRevision === undefined
        ? null
        : {
            playerId: profile.playerId,
            name: profile.name,
            revision: profile.nameRevision,
          };
    return session;
  }
  private update(state: AuthSnapshot): void {
    this.value = state;
    this.publish(state);
  }
  private begin(): number {
    const epoch = ++this.epoch;
    this.invalidate();
    this.update({
      ...this.value,
      status: "loading",
      session: null,
      busy: true,
      error: "",
      retryAvailable: false,
    });
    return epoch;
  }
  private accept(epoch: number, session: SessionResponse): boolean {
    if (epoch !== this.epoch) return false;
    this.storage.setItem("frontier.token", session.token);
    this.storage.removeItem("frontier.signed-out");
    this.update({
      status: "ready",
      session: this.withNickname(session),
      busy: false,
      error: "",
      logoutPending: false,
      retryAvailable: false,
    });
    return true;
  }
  private failure(
    epoch: number,
    error: Error,
    fallback: SessionResponse | null = null,
    guestSessionRetry = false,
  ): false {
    if (epoch !== this.epoch) return false;
    const definitive =
      error instanceof ApiError &&
      error.status >= 400 &&
      error.status < 500 &&
      error.status !== 401;
    if (fallback && definitive) this.storage.removeItem("frontier.signed-out");
    this.update({
      status: fallback && definitive ? "ready" : "recovery",
      session: definitive && fallback ? this.withNickname(fallback) : null,
      busy: false,
      error: error.message,
      logoutPending: this.value.logoutPending,
      retryAvailable:
        !(fallback && definitive) &&
        (!!this.storage.getItem("frontier.token") || guestSessionRetry),
    });
    return false;
  }
  async bootstrap(): Promise<boolean> {
    if (this.storage.getItem("frontier.signed-out")) {
      this.update({
        status: "signed-out",
        session: null,
        busy: false,
        error: "",
        logoutPending: !!this.storage.getItem("frontier.token"),
        retryAvailable: false,
      });
      return false;
    }
    const token = this.storage.getItem("frontier.token") ?? "";
    const epoch = this.begin();
    const request =
      this.bootstrapRequest?.token === token
        ? this.bootstrapRequest.request
        : this.api.session(token);
    this.bootstrapRequest = { token, request };
    try {
      return this.accept(epoch, await request);
    } catch (error) {
      return this.failure(
        epoch,
        error instanceof Error
          ? error
          : new Error("Не удалось восстановить пилота"),
        null,
        token === "",
      );
    } finally {
      if (this.bootstrapRequest?.request === request)
        this.bootstrapRequest = null;
    }
  }
  async retry(): Promise<boolean> {
    const token = this.storage.getItem("frontier.token");
    if (this.value.busy || (!token && !this.value.retryAvailable)) return false;
    return this.restoreToken(token ?? "");
  }
  private async restoreToken(token: string): Promise<boolean> {
    const epoch = this.begin();
    try {
      return this.accept(epoch, await this.api.session(token));
    } catch (error) {
      return this.failure(
        epoch,
        error instanceof Error
          ? error
          : new Error("Не удалось восстановить пилота"),
        null,
        token === "",
      );
    }
  }
  async playAsGuest(): Promise<boolean> {
    return this.restoreToken("");
  }
  async chooseFaction(input: FactionRequest): Promise<boolean> {
    const previous = this.value.session;
    if (!previous || this.value.busy) return false;
    const epoch = this.begin();
    try {
      return this.accept(
        epoch,
        await this.api.chooseFaction(previous.token, input),
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        try {
          const canonical = await this.api.session(previous.token);
          if (!this.accept(epoch, canonical)) return false;
          this.update({ ...this.value, error: error.message });
          return false;
        } catch (refreshError) {
          return this.failure(
            epoch,
            refreshError instanceof Error
              ? refreshError
              : new Error("Не удалось подтвердить выбранную фракцию"),
          );
        }
      }
      return this.failure(
        epoch,
        error instanceof Error
          ? error
          : new Error("Не удалось выбрать фракцию"),
        { ...previous },
      );
    }
  }
  async login(input: LoginRequest): Promise<boolean> {
    const epoch = this.begin();
    try {
      return this.accept(epoch, await this.api.login(input));
    } catch (error) {
      return this.failure(
        epoch,
        error instanceof Error ? error : new Error("Не удалось войти"),
      );
    }
  }
  async register(input: RegisterRequest): Promise<boolean> {
    if (this.value.busy) return false;
    const recovering = this.value.status === "recovery";
    const retainedToken = this.storage.getItem("frontier.token");
    let previous = this.value.session;
    if (!previous && recovering && !retainedToken) {
      this.update({
        ...this.value,
        error: this.value.retryAvailable
          ? "Повтори подключение или начни новым гостем."
          : "Сначала начни новым гостем.",
      });
      return false;
    }
    const epoch = this.begin();
    try {
      if (!previous) {
        previous = await this.api.session(
          recovering ? (retainedToken ?? "") : "",
        );
        if (epoch !== this.epoch) return false;
        this.storage.setItem("frontier.token", previous.token);
      }
      return this.accept(epoch, await this.api.register(previous.token, input));
    } catch (error) {
      const failure =
        error instanceof Error
          ? error
          : new Error("Не удалось зарегистрироваться");
      if (!(failure instanceof ApiError) || failure.status >= 500)
        return this.failure(
          epoch,
          new Error(
            "Регистрация могла завершиться. Попробуй войти с тем же логином и паролем.",
          ),
        );
      return this.failure(epoch, failure, previous);
    }
  }
  async logout(): Promise<boolean> {
    const token =
      this.value.session?.token ?? this.storage.getItem("frontier.token");
    const epoch = this.begin();
    this.storage.setItem("frontier.signed-out", "1");
    if (!token) {
      this.update({
        status: "signed-out",
        session: null,
        busy: false,
        error: "",
        logoutPending: false,
        retryAvailable: false,
      });
      return true;
    }
    try {
      await this.api.logout(token);
      if (epoch !== this.epoch) return false;
      this.storage.removeItem("frontier.token");
      this.update({
        status: "signed-out",
        session: null,
        busy: false,
        error: "",
        logoutPending: false,
        retryAvailable: false,
      });
      return true;
    } catch {
      if (epoch === this.epoch)
        this.update({
          status: "signed-out",
          session: null,
          busy: false,
          logoutPending: true,
          retryAvailable: false,
          error:
            "Выход не завершён. Повтори выход.",
        });
      return false;
    }
  }
  expire(message: string): void {
    ++this.epoch;
    this.invalidate();
    this.update({
      status: "recovery",
      session: null,
      busy: false,
      error: message,
      logoutPending: false,
      retryAvailable: !!this.storage.getItem("frontier.token"),
    });
  }
  cancel(): void {
    ++this.epoch;
    this.invalidate();
  }
}
